import { useEffect, useRef, useState } from 'react';
import igv from 'igv';
import './index.css';
import { Icon } from '../../shared/ui/Icon';
import { DataDescriptions, DataGeneral, DataGeneralGenatator } from './data';
import { Button } from '../../shared/ui/Button';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { appApi, extractRequestId, host, useUploadMutation } from '../../app/api';
import { resetState } from '../../app/slice';
import { Link } from 'react-router-dom';
import { AIChat } from '../AIChat';
import {
    SelectedFeature,
    setGffUrl,
    setRequestId,
    setSelectedFeature,
    setVisibleRegion,
} from '../../app/chatSlice';
import { buildAncestry, findFeatureFromPopover, GFFFeature, ParsedGFF, parseGFF } from '../AIChat/gff';

const Genome = () => {
    const dispatch = useDispatch();
    const resetData = () => {
        dispatch(resetState());
        dispatch(appApi.util.resetApiState());
    };

    const { model, importance } = useSelector((state: RootState) => state.app);
    const selectedFeature = useSelector((state: RootState) => state.chat.selectedFeature);
    const isSelectedPanelOpen = useSelector((state: RootState) => state.chat.isSelectedPanelOpen);
    const [_, { isSuccess: isUploadSuccess, data: dataUpload }] = useUploadMutation({
        fixedCacheKey: 'shared-upload',
    });
    const [isShowText, setIsShowText] = useState(false);
    const browserRef = useRef<any>(null);
    const parsedGffRef = useRef<ParsedGFF | null>(null);
    const requestId = dataUpload?.archive?.split('/')[3]?.replace('_archive.zip', '');

    const gffPath = dataUpload?.gff_file ?? dataUpload?.gff;

    useEffect(() => {
        console.log('[AIChat] dataUpload', dataUpload);
        if (model?.value !== 'genatator-pipeline' || !gffPath) {
            console.log('[AIChat] init skipped', {
                model: model?.value,
                gffPath,
                fai_file: dataUpload?.fai_file,
            });
            parsedGffRef.current = null;
            dispatch(setRequestId(null));
            dispatch(setGffUrl(null));
            return;
        }
        // The backend fetches gff_url server-side via httpx, so it must be absolute.
        // In prod `host` is empty (frontend talks to nginx via relative paths), so we
        // anchor to window.location.origin here.
        const relativeOrAbsolute = `${host}${gffPath}`;
        const gffUrl = /^https?:\/\//i.test(relativeOrAbsolute)
            ? relativeOrAbsolute
            : `${window.location.origin}${relativeOrAbsolute}`;
        const reqId = extractRequestId(dataUpload?.fai_file);
        console.log('[AIChat] init chat session', { requestId: reqId, gffUrl });
        dispatch(setRequestId(reqId));
        dispatch(setGffUrl(gffUrl));

        let cancelled = false;
        fetch(gffUrl)
            .then((res) => res.text())
            .then((text) => {
                if (cancelled) return;
                const parsed = parseGFF(text);
                parsedGffRef.current = parsed;
                console.log('[AIChat] GFF parsed', {
                    featuresCount: parsed.features.length,
                    byIdSize: parsed.byId.size,
                    firstFew: parsed.features.slice(0, 3),
                });
            })
            .catch((err) => {
                console.warn('[AIChat] GFF fetch/parse failed', err);
                if (!cancelled) parsedGffRef.current = null;
            });
        return () => {
            cancelled = true;
        };
    }, [model?.value, gffPath, dataUpload?.fai_file, dispatch]);

    useEffect(() => {
        if (isUploadSuccess && dataUpload) {
            let cancelled = false;
            const igvDiv = document.getElementById('igv-div');
            const tracks = [];

            if (dataUpload?.acceptor_bed_file) {
                tracks.push({
                    name: 'acceptor',
                    url: `${host}${dataUpload?.acceptor_bed_file}`,
                });
            }

            if (dataUpload?.bed?.length) {
                dataUpload.bed.forEach((bedFile: string) => {
                    tracks.push({
                        url: `${host}${bedFile}`,
                    });
                });
            }

            if (gffPath) {
                tracks.push({
                    name: 'annotations',
                    url: `${host}${gffPath}`,
                    format: 'gff',
                    type: 'annotation',
                });
            }

            const options = {
                reference: {
                    fastaURL: `${host}${dataUpload?.fasta_file}`,
                    indexURL: `${host}${dataUpload?.fai_file}`,
                    tracks: [...tracks],
                },
            };

            igv.createBrowser(igvDiv, options).then((browser: any) => {
                // Effect re-ran (or unmounted) before the browser finished building —
                // discard this orphan instance to avoid a duplicate IGV.
                if (cancelled) {
                    igv.removeBrowser(browser);
                    return;
                }
                browserRef.current = browser;
                if (model?.value === 'genatator-pipeline') {
                    const publishRegion = (rf: any) => {
                        if (!rf) return;
                        const region = {
                            chromosome: rf.chr ?? rf.chromosome ?? '',
                            start: Math.floor(rf.start ?? 0),
                            end: Math.floor(rf.end ?? 0),
                        };
                        console.log('[AIChat] visibleRegion', region);
                        dispatch(setVisibleRegion(region));
                    };

                    // Seed visible region right after the browser is built so the very
                    // first user message already has chromosome/start/end set, even if
                    // they never scrolled IGV.
                    const initialFrame = Array.isArray(browser.referenceFrameList)
                        ? browser.referenceFrameList[0]
                        : null;
                    publishRegion(initialFrame);

                    browser.on('locuschange', (referenceFrameList: any) => {
                        const rf = Array.isArray(referenceFrameList) ? referenceFrameList[0] : referenceFrameList;
                        publishRegion(rf);
                    });

                    browser.on('trackclick', (track: any, popoverData: any) => {
                        const isAnnotation =
                            track?.config?.format === 'gff' ||
                            track?.config?.format === 'gff3' ||
                            track?.config?.type === 'annotation';
                        console.log('[AIChat] trackclick', {
                            isAnnotation,
                            trackFormat: track?.config?.format,
                            popoverDataCount: Array.isArray(popoverData) ? popoverData.length : null,
                            popoverData,
                        });
                        if (!isAnnotation || !Array.isArray(popoverData)) return undefined;

                        const parsed = parsedGffRef.current;
                        console.log('[AIChat] trackclick parsed available?', !!parsed);
                        if (parsed) {
                            const feat = findFeatureFromPopover(parsed, popoverData);
                            console.log('[AIChat] trackclick resolved feature', feat);
                            if (feat) {
                                const chain = buildAncestry(feat, parsed.byId);
                                const toSelected = (f: GFFFeature, deeper?: SelectedFeature): SelectedFeature => ({
                                    id: f.attributes.ID || `${f.seqid}:${f.start}-${f.end}`,
                                    type: f.type,
                                    chromosome: f.seqid,
                                    start: f.start,
                                    end: f.end,
                                    strand: f.strand,
                                    attributes: f.attributes,
                                    attributeString: f.attributeString,
                                    parent: deeper,
                                });
                                // chain is [self, parent, grandparent, ...].
                                // Wrap into nested SelectedFeature: self.parent = its direct parent
                                // (which in turn may have its own parent reference).
                                let nested: SelectedFeature | undefined;
                                for (let i = chain.length - 1; i >= 0; i--) {
                                    nested = toSelected(chain[i], nested);
                                }
                                if (nested) dispatch(setSelectedFeature(nested));
                                return false; // suppress IGV's native popover, we use our own panel
                            }
                            // Click landed on the annotation track but resolved to no
                            // feature (empty inter-genic region) — clear the selection.
                            console.log('[AIChat] trackclick empty region -> clearing selection');
                            dispatch(setSelectedFeature(null));
                            return false;
                        }

                        // Fallback: extract directly from popoverData (no parent chain).
                        const attributes: Record<string, string> = {};
                        let id = '';
                        let type = 'feature';
                        let chromosome = '';
                        let start = 0;
                        let end = 0;
                        let strand: '+' | '-' | '.' | '?' | undefined;

                        popoverData.forEach((nv: { name?: string; value?: string }) => {
                            if (!nv?.name) return;
                            const key = nv.name.replace(/:$/, '').trim();
                            const value = (nv.value ?? '').toString();
                            const lower = key.toLowerCase();

                            if (lower === 'location') {
                                const [chr, range] = value.split(':');
                                chromosome = chr ?? '';
                                if (range) {
                                    const [s, e] = range.split('-').map((p) => Number(p.replace(/,/g, '')));
                                    if (!Number.isNaN(s)) start = s;
                                    if (!Number.isNaN(e)) end = e;
                                }
                            } else if (lower === 'name' || lower === 'id') {
                                id = value;
                                attributes[key] = value;
                            } else if (lower === 'type') {
                                type = value;
                                attributes[key] = value;
                            } else if (lower === 'strand') {
                                if (value === '+' || value === '-' || value === '.' || value === '?') strand = value;
                                attributes[key] = value;
                            } else {
                                attributes[key] = value;
                            }
                        });

                        if (id || chromosome) {
                            dispatch(
                                setSelectedFeature({
                                    id: id || `${chromosome}:${start}-${end}`,
                                    type,
                                    chromosome,
                                    start,
                                    end,
                                    strand,
                                    attributes,
                                })
                            );
                        } else {
                            // Empty region click — clear the selection.
                            dispatch(setSelectedFeature(null));
                        }
                        return false; // suppress IGV's native popover, we use our own panel
                    });
                }

                if (importance) {
                    let currentImportanceFeature = '';
                    browser.on('trackclick', (track: any, popoverData: any) => {
                        let symbol = null;

                        if (track.config.format === 'bedgraph') {
                            popoverData.forEach((nameValue: any) => {
                                if (nameValue.name?.toLowerCase() === 'position:') {
                                    const [start, end] = nameValue.value.split('-');
                                    symbol = `${track._name}_${start}_${end}`;
                                }
                            });
                        } else {
                            // Обработка других форматов (например, bed)
                            for (let i = 0; i < popoverData.length; i++) {
                                const nameValue = popoverData[i];

                                if (nameValue.name?.toLowerCase() === 'name') {
                                    // Если есть 'Name', используем его для символа
                                    symbol = nameValue.value;
                                    break; // Прерываем цикл
                                }

                                if (nameValue.name?.toLowerCase() === 'location') {
                                    const locationValue = nameValue.value.split(':')[1]; // Пример: "dm6_dna:6,001-8,000"
                                    if (locationValue) {
                                        const [start, end] = locationValue.split('-');
                                        symbol = `${track._name}_${start.replace(',', '')}_${end.replace(',', '')}`;
                                    }
                                }
                            }
                        }

                        console.log('symbol: ' + symbol);
                        if (symbol && currentImportanceFeature != symbol) {
                            console.log('Updating importance track');

                            // if there is previously loaded track, remove it
                            if (currentImportanceFeature != '') {
                                console.log('removing track ' + currentImportanceFeature);
                                browser.removeTrackByName(currentImportanceFeature);
                            }

                            currentImportanceFeature = `Importance ${symbol}`;

                            // load feature importance track
                            const trackName = './' + symbol + '.bedGraph';
                            const trackUrl = `${host}/generated/${
                                model?.value
                            }/${requestId}_attributions_${trackName.slice(2)}`;
                            console.log('track_name: ' + trackName);
                            const configTrack = {
                                url: trackUrl,
                                format: 'wig',
                                name: `Importance ${symbol}`,
                            };
                            console.log(browser.loadTrack(configTrack));
                        }

                        // Continue with the default pop-over behavior
                        return undefined;
                    });
                }
            });

            return () => {
                cancelled = true;
                if (browserRef.current) {
                    igv.removeBrowser(browserRef.current);
                    browserRef.current = null;
                }
            };
        }
    }, [isUploadSuccess, dataUpload]);

    // Highlight the currently selected feature on the IGV track via a region-of-interest
    // overlay, so the user can see what the Selected element panel refers to.
    useEffect(() => {
        const browser = browserRef.current;
        if (!browser || typeof browser.clearROIs !== 'function') return;

        browser.clearROIs();
        if (selectedFeature && isSelectedPanelOpen) {
            browser.loadROI({
                name: `Selected: ${selectedFeature.id}`,
                color: 'rgba(20, 214, 188, 0.25)',
                features: [
                    {
                        chr: selectedFeature.chromosome,
                        // GFF is 1-based inclusive; IGV features are 0-based half-open.
                        start: Math.max(0, selectedFeature.start - 1),
                        end: selectedFeature.end,
                    },
                ],
            });
        }
    }, [selectedFeature, isSelectedPanelOpen]);

    return (
        <>
            <div className="container">
                <div className="slider">
                    <div onClick={() => setIsShowText((prevState) => !prevState)} className="slider__header">
                        <div className="slider__title">
                            <Icon name={'question'} />
                            Help
                        </div>
                        {isShowText ? <Icon name={'arrow-top'} /> : <Icon name={'arrow-down'} />}
                    </div>
                    {isShowText && (
                        <div className="slider__content">
                            <div>{model && DataDescriptions[model.value]}</div>
                            {model?.value === 'genatator-pipeline' ? DataGeneralGenatator : DataGeneral}
                        </div>
                    )}
                </div>
                <div className="F-Genome__archive">
                    <Button type={'link'} href={dataUpload && dataUpload?.archive}>
                        Download results
                    </Button>
                </div>
                <div className="F-Genome__new-job">
                    <Link onClick={resetData} to="/" className="F-Genome__new-job-link">
                        New job
                    </Link>
                </div>
            </div>
            <div className="F-Genome" id="igv-div" />
            {model?.value === 'genatator-pipeline' && <AIChat />}
        </>
    );
};

export { Genome };
